/**
 * Catalog scope of a manufacturer's or distributor's organisation
 * (managed_orgs.catalog_brand, docs/partner-login.md). Per category: the
 * scope's products where it has any, generic where it has none, never another
 * brand. So a GANZ planner shows GANZ cameras and recorders with no generic
 * camera, and a generic PoE switch.
 *
 * A manufacturer's scope names one brand ("GANZ"). A distributor's names the
 * brands its shop carries, its own brand first ("Kenik, Hikvision, Dahua"):
 * the same field, a comma-separated list.
 *
 * Client-side product scoping, as the contract names it: the shards stay
 * public, the pickers just show the organisation's range.
 */
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/** The brands a scope names, in order. */
export function scopeBrands(scope: string | null | undefined): string[] {
  return (scope ?? '').split(/[,;|]/).map((b) => b.trim()).filter(Boolean);
}

/** The organisation's own brand: the first one its scope names. Its own products are filed under it. */
export function primaryScopeBrand(scope: string | null | undefined): string | null {
  return scopeBrands(scope)[0] ?? null;
}

function matchesBrand(manufacturer: string | null | undefined, brand: string): boolean {
  const m = (manufacturer ?? '').trim().toLowerCase();
  const b = brand.trim().toLowerCase();
  return !!b && (m === b || m.startsWith(`${b} `));
}

/**
 * A manufacturer belongs to the scope: "GANZ" matches "GANZ", "Ganz", "GANZ
 * Security"; a distributor's list matches any brand it names.
 */
export function brandMatches(manufacturer: string | null | undefined, brand: string): boolean {
  return scopeBrands(brand).some((b) => matchesBrand(manufacturer, b));
}

export function isGenericMaker(manufacturer: string | null | undefined): boolean {
  const m = (manufacturer ?? '').trim().toLowerCase();
  return m === '' || m === 'generic';
}

/** Camera manufacturers the picker may show. Without a brand: everything. */
export function scopeCameraManufacturers<T extends { name: string; models: unknown[] }>(list: T[], brand: string | null): T[] {
  if (!brand) return list;
  const own = list.filter((m) => brandMatches(m.name, brand) && m.models.length > 0);
  return own.length > 0 ? own : list.filter((m) => isGenericMaker(m.name));
}

/** Whether generic cameras (the quick-pick tiles) may be offered. */
export function genericCamerasAllowed(list: Array<{ name: string; models: unknown[] }> | null, brand: string | null): boolean {
  if (!brand) return true;
  if (!list) return false;
  return !list.some((m) => brandMatches(m.name, brand) && m.models.length > 0);
}

/**
 * A brand's own equipment: entries verified against its datasheets. Unverified
 * rows scraped from aggregators (old DVRs, discontinued lines) do not count, so
 * a category falls back to generic until the manufacturer's current range is in.
 */
export function isOwnEquipment(e: { manufacturer: string; hidden?: boolean; verified?: boolean }, brand: string): boolean {
  return !e.hidden && e.verified === true && brandMatches(e.manufacturer, brand);
}

/** Categories in which the brand has at least one verified catalog entry. */
export function brandCategories(entries: ReadonlyArray<{ manufacturer: string; category: string; hidden?: boolean; verified?: boolean }>, brand: string): Set<string> {
  const out = new Set<string>();
  for (const e of entries) if (isOwnEquipment(e, brand)) out.add(e.category);
  return out;
}

/** Catalog entries the equipment picker may show. */
export function scopeEquipmentEntries<T extends { manufacturer: string; category: string; hidden?: boolean; verified?: boolean }>(entries: T[], brand: string | null, ownCategories: Set<string>): T[] {
  if (!brand) return entries;
  return entries.filter((e) => (ownCategories.has(e.category) ? isOwnEquipment(e, brand) : isGenericMaker(e.manufacturer)));
}

/** Whether a generic quick pick of this category may be offered. */
export function genericEquipmentAllowed(category: string, brand: string | null, ownCategories: Set<string>): boolean {
  return !brand || !ownCategories.has(category);
}

// ── Current user's scope ───────────────────────────────────────────────────

let current: string | null = null;
let loadedFor: string | null | undefined;
const listeners = new Set<(b: string | null) => void>();

function set(brand: string | null) {
  if (brand === current) return;
  current = brand;
  listeners.forEach((l) => l(brand));
}

async function load(userId: string | null, force = false): Promise<void> {
  if (!force && userId === loadedFor) return;
  loadedFor = userId;
  if (!userId) { set(null); return; }
  let brand: string | null = null;
  try {
    const { data, error } = (await supabase.rpc('my_catalog_scope' as never)) as { data: unknown; error: unknown };
    brand = !error && typeof data === 'string' && data.trim() ? data.trim() : null;
  } catch { /* full catalog on any failure */ }
  // A later sign-in may have started meanwhile; its answer wins.
  if (loadedFor === userId) set(brand);
}

/** Re-read the scope, e.g. after the user joined an organisation. */
export async function refreshCatalogScope(): Promise<void> {
  const { data } = await supabase.auth.getSession();
  await load(data.session?.user.id ?? null, true);
}

let subscribed = false;
function ensureSubscribed() {
  if (subscribed || typeof window === 'undefined') return;
  subscribed = true;
  void supabase.auth.getSession().then(({ data }) => load(data.session?.user.id ?? null));
  supabase.auth.onAuthStateChange((_event, session) => { void load(session?.user.id ?? null); });
}

/** The brand the signed-in user's catalog is limited to, or null for the full catalog. */
export function useCatalogBrand(): string | null {
  const [brand, setBrand] = useState<string | null>(current);
  useEffect(() => {
    ensureSubscribed();
    listeners.add(setBrand);
    setBrand(current);
    return () => { listeners.delete(setBrand); };
  }, []);
  return brand;
}
